// Admin read-only request detail — route /all-requests/:requestId, reached
// by clicking a request reference on the All-requests list.
//
// STRICTLY READ-ONLY. It shows everything the request contains — header,
// line items as the buyer submitted them, attachments, quotation history,
// the notes log, and an activity timeline — and NOTHING that changes state.
// Assignment happens on the list (AllRequestsPage's inline picker); this
// screen deliberately does not duplicate it or any other action.
//
// Reuses existing components: AttachmentPanel from the sales workspace (with
// its `fileUrlBase` pointed at the admin upload routes), the shared
// SignatureElement, and — for quotation history — a link straight to the
// read-only proforma viewer at /all-proformas/:id.

import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import {
  useAdminRequestDetail,
  useAdminRequestUploads,
  useAdminRequestNotes,
} from '../hooks/useAdminRequests.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import AttachmentPanel from '../components/AttachmentPanel.jsx';
import { SignatureElement } from 'shared/components/SignatureElement/index.js';
import { statusLabel } from '../i18n.js';
import { formatMoney } from 'shared/money.js';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import styles from './AdminRequestDetailPage.module.css';

// The recurring proforma-number rule (SignatureElement) reflects the latest
// quotation's standing, same vocabulary the approval screen uses.
function signatureStatus(quotations) {
  if (!quotations || quotations.length === 0) return 'unallocated';
  const latest = quotations[quotations.length - 1];
  if (latest.status === 'sent' || latest.status === 'accepted') return 'stamped';
  if (latest.status === 'approved') return 'approved';
  if (quotations.some((q) => q.status === 'superseded') || latest.revision > 1) return 'revised';
  return 'unallocated';
}

function HeaderRow({ label, children }) {
  return (
    <div className={styles.metaRow}>
      <span className={styles.metaLabel}>{label}</span>
      <span className={styles.metaValue}>{children}</span>
    </div>
  );
}

export default function AdminRequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  const detailQuery = useAdminRequestDetail(requestId);
  const uploadsQuery = useAdminRequestUploads(requestId);
  const notesQuery = useAdminRequestNotes(requestId);

  if (detailQuery.isLoading) {
    return <LoadingState variant="page" />;
  }
  if (detailQuery.isError) {
    return (
      <ErrorState
        messageId="requestDetail.errorBody"
        error={detailQuery.error}
        onRetry={detailQuery.refetch}
      />
    );
  }

  const request = detailQuery.data;
  const quotations = request.quotations || [];
  const timeline = request.timeline || [];
  const uploads = uploadsQuery.data || [];
  const notes = notesQuery.data || [];
  const notificationDeliveries = request.notificationDeliveries || [];

  const orgName =
    lang === 'am' && request.organization_name_am
      ? request.organization_name_am
      : request.organization_name;
  const latestProformaNo = [...quotations].reverse().find((q) => q.proformaNo)?.proformaNo || null;

  return (
    <div className={styles.page}>
      <button type="button" className={styles.backLink} onClick={() => navigate('/all-requests')}>
        {t('requestDetail.back')}
      </button>

      <p className={styles.readOnlyHint}>{t('requestDetail.readOnlyHint')}</p>

      <div className={styles.header}>
        <SignatureElement
          proformaNo={latestProformaNo}
          status={signatureStatus(quotations)}
          lang={lang}
        />
        <div className={styles.headerMeta}>
          <h1 className={`${styles.refNo} tabularNums`}>{request.ref_no}</h1>
          <HeaderRow label={t('requestDetail.header.status')}>
            {statusLabel(request.status, lang)}
          </HeaderRow>
          <HeaderRow label={t('requestDetail.header.submitted')}>
            <span className="tabularNums">{formatDate(request.created_at, lang) || '—'}</span>
          </HeaderRow>
          <HeaderRow label={t('requestDetail.header.neededBy')}>
            <span className="tabularNums">{formatDate(request.needed_by, lang) || '—'}</span>
          </HeaderRow>
          <HeaderRow label={t('requestDetail.header.purpose')}>
            {request.purpose || t('requestDetail.header.none')}
          </HeaderRow>
          <HeaderRow label={t('requestDetail.header.assignee')}>
            {request.assigned_to_name || t('requestDetail.header.unassigned')}
          </HeaderRow>
          <HeaderRow label={t('requestDetail.header.submittedBy')}>
            {request.submitted_by_name || t('requestDetail.header.none')}
          </HeaderRow>
          {request.buyer_note && (
            <HeaderRow label={t('requestDetail.header.buyerNote')}>{request.buyer_note}</HeaderRow>
          )}
        </div>
      </div>

      <hr className="hairline" />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('requestDetail.contact.title')}</h2>
        <div className={styles.contactGrid}>
          <HeaderRow label={t('requestDetail.contact.person')}>
            {orgName}
            {request.organization_contact_name ? ` · ${request.organization_contact_name}` : ''}
          </HeaderRow>
          <HeaderRow label={t('requestDetail.contact.email')}>
            {request.organization_contact_email || t('requestDetail.header.none')}
          </HeaderRow>
          <HeaderRow label={t('requestDetail.contact.phone')}>
            <span className="tabularNums">
              {request.organization_contact_phone || t('requestDetail.header.none')}
            </span>
          </HeaderRow>
          <HeaderRow label={t('requestDetail.contact.address')}>
            {request.organization_address || t('requestDetail.header.none')}
          </HeaderRow>
          <HeaderRow label={t('requestDetail.contact.tin')}>
            <span className="tabularNums">
              {request.organization_tin || t('requestDetail.header.none')}
            </span>
          </HeaderRow>
        </div>
      </section>

      <hr className="hairline" />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('requestDetail.lines.title')}</h2>
        <p className={styles.muted}>{t('requestDetail.lines.subtitle')}</p>
        {request.lines.length === 0 ? (
          <p className={styles.muted}>{t('requestDetail.lines.empty')}</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('requestDetail.lines.col.line')}</th>
                <th>{t('requestDetail.lines.col.description')}</th>
                <th>{t('requestDetail.lines.col.unit')}</th>
                <th>{t('requestDetail.lines.col.qty')}</th>
                <th>{t('requestDetail.lines.col.matched')}</th>
              </tr>
            </thead>
            <tbody>
              {request.lines.map((line) => {
                const matchedName =
                  lang === 'am' && line.matched_product_name_am
                    ? line.matched_product_name_am
                    : line.matched_product_name;
                return (
                  <tr key={line.id}>
                    <td className="tabularNums">{line.line_no}</td>
                    <td className={styles.lineDescription}>{line.raw_description}</td>
                    <td>{line.raw_unit || '—'}</td>
                    <td className="tabularNums">{line.qty ?? '—'}</td>
                    <td>
                      {matchedName ? (
                        <span>
                          {matchedName}
                          {line.matched_product_sku && (
                            <span className={`${styles.sku} tabularNums`}>
                              {' '}
                              {line.matched_product_sku}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className={styles.muted}>{t('requestDetail.lines.noMatch')}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <hr className="hairline" />

      {uploads.length === 0 ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('requestDetail.attachments.title')}</h2>
          <p className={styles.muted}>{t('requestDetail.attachments.empty')}</p>
        </section>
      ) : (
        <AttachmentPanel
          requestId={requestId}
          uploads={uploads}
          fileUrlBase={`/admin/requests/${requestId}/uploads`}
        />
      )}

      <hr className="hairline" />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('requestDetail.quotations.title')}</h2>
        {quotations.length === 0 ? (
          <p className={styles.muted}>{t('requestDetail.quotations.none')}</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('requestDetail.quotations.col.proforma')}</th>
                <th>{t('requestDetail.quotations.col.revision')}</th>
                <th>{t('requestDetail.quotations.col.status')}</th>
                <th>{t('requestDetail.quotations.col.total')}</th>
                <th aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {quotations.map((q) => (
                <tr key={q.id}>
                  <td className={`${styles.refNoCell} tabularNums`}>
                    {q.proformaNo || (
                      <span className={styles.muted}>{t('requestDetail.quotations.noNumber')}</span>
                    )}
                  </td>
                  <td className="tabularNums">{q.revision}</td>
                  <td>{statusLabel(q.status, lang)}</td>
                  <td className="tabularNums">
                    {q.total ? `${formatMoney(q.total)} ${q.currency}` : '—'}
                  </td>
                  <td>
                    <Link to={`/all-proformas/${q.id}`} className={styles.viewLink}>
                      {t('requestDetail.quotations.view')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <hr className="hairline" />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('requestDetail.notes.title')}</h2>
        {notes.length === 0 ? (
          <p className={styles.muted}>{t('requestDetail.notes.empty')}</p>
        ) : (
          <ul className={styles.notesList}>
            {notes.map((note) => (
              <li
                key={note.id}
                className={`${styles.note} ${
                  note.authorType === 'staff' ? styles.noteStaff : styles.noteBuyer
                }`}
              >
                <div className={styles.noteHead}>
                  <span className={styles.noteAuthor}>
                    {note.authorName ||
                      (note.authorType === 'staff'
                        ? t('requestDetail.notes.fromStaff')
                        : t('requestDetail.notes.fromBuyer'))}
                  </span>
                  <span className={`${styles.noteWhen} tabularNums`}>
                    {formatDateTime(note.createdAt, lang) || '—'}
                  </span>
                </div>
                <p className={styles.noteBody}>{note.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <hr className="hairline" />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('requestDetail.timeline.title')}</h2>
        {timeline.length === 0 ? (
          <p className={styles.muted}>{t('requestDetail.timeline.empty')}</p>
        ) : (
          <ol className={styles.timeline}>
            {timeline.map((entry, i) => (
              <li key={`${entry.key}-${i}`} className={styles.timelineRow}>
                <span className={`${styles.timelineWhen} tabularNums`}>
                  {formatDateTime(entry.at, lang) || '—'}
                </span>
                <span className={styles.timelineEvent}>
                  {t(`requestDetail.timeline.event.${entry.key}`)}
                  {entry.revision > 1 && (
                    <span className={styles.timelineTag}>
                      {t('requestDetail.timeline.revisionTag', { n: entry.revision })}
                    </span>
                  )}
                  {entry.actorName && (
                    <span className={styles.timelineActor}>
                      {' '}
                      {t('requestDetail.timeline.by', { name: entry.actorName })}
                    </span>
                  )}
                  {entry.key === 'assigned' && entry.detail && (
                    <span className={styles.timelineActor}>
                      {' '}
                      {t('requestDetail.timeline.assignedTo', { name: entry.detail })}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <hr className="hairline" />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('requestDetail.notifications.title')}</h2>
        {notificationDeliveries.length === 0 ? (
          <p className={styles.muted}>{t('requestDetail.notifications.empty')}</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('requestDetail.notifications.col.when')}</th>
                <th>{t('requestDetail.notifications.col.type')}</th>
                <th>{t('requestDetail.notifications.col.channel')}</th>
                <th>{t('requestDetail.notifications.col.recipient')}</th>
                <th>{t('requestDetail.notifications.col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {notificationDeliveries.map((delivery) => {
                const typeLabel = t(`requestDetail.notifications.type.${delivery.type}`);
                const channelLabel = t(`requestDetail.notifications.channel.${delivery.channel}`);
                const recipientLabel = t(
                  `requestDetail.notifications.recipient.${delivery.recipient_type}`
                );
                const statusLabelText = t(`requestDetail.notifications.status.${delivery.status}`);
                const isFailure = delivery.status === 'failed';
                const when = delivery.sent_at || delivery.created_at;
                return (
                  <tr key={delivery.id}>
                    <td className="tabularNums">{formatDateTime(when, lang) || '—'}</td>
                    <td>{typeLabel}</td>
                    <td>{channelLabel}</td>
                    <td>
                      {recipientLabel}
                      {delivery.destination && (
                        <span className={styles.sku}> · {delivery.destination}</span>
                      )}
                    </td>
                    <td>
                      <span className={isFailure ? styles.notificationFailed : undefined}>
                        {statusLabelText}
                      </span>
                      {delivery.attempts > 0 && (
                        <span className={`${styles.sku} tabularNums`}>
                          {' '}
                          {t('requestDetail.notifications.attempts', { n: delivery.attempts })}
                        </span>
                      )}
                      {isFailure && delivery.last_error && (
                        <p className={styles.notificationError}>{delivery.last_error}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
